import { useTranslation } from 'react-i18next';

import type { PlayerStatsAggregate } from '@/types/qriots';

interface StatsCardProps {
  data: PlayerStatsAggregate;
}

const ROLE_EMOJI: Record<string, string> = {
  TOP: '⚔️',
  JUNGLE: '🌳',
  MIDDLE: '✨',
  BOTTOM: '🏹',
  UTILITY: '🛡️',
  '': '❓',
};

export function StatsCard({ data }: StatsCardProps) {
  const { t } = useTranslation('riot');
  const wrPct = (data.overallWinRate * 100).toFixed(1);
  const streak = data.currentStreak;

  return (
    <div className="mx-4 flex flex-col gap-4 rounded-xl bg-bg-secondary p-4">
      {/* Header */}
      <div className="flex items-baseline justify-between gap-2">
        <p className="text-sm font-medium text-fg">
          {t('stats.windowLabel', { count: data.matchesAnalyzed })} · {data.region}
        </p>
        {streak.type !== 'none' && streak.count > 1 && (
          <span
            className={[
              'text-[10px] font-bold uppercase tracking-wider',
              streak.type === 'win' ? 'text-success' : 'text-fg-muted',
            ].join(' ')}
          >
            {streak.type === 'win'
              ? t('stats.streakWin', { count: streak.count })
              : t('stats.streakLoss', { count: streak.count })}
          </span>
        )}
      </div>

      {/* Overall */}
      <section>
        <p className="text-[10px] font-bold uppercase tracking-wider text-fg-muted">
          {t('stats.overall')}
        </p>
        <div className="mt-1 text-sm text-fg">
          <p className="font-bold">
            {t('record.winsLosses', { wins: data.totalWins, losses: data.totalLosses })} · {wrPct}% {t('stats.wr')}
          </p>
          <p className="text-fg-muted">
            {t('stats.avgKda', {
              k: data.avgKills.toFixed(1),
              d: data.avgDeaths.toFixed(1),
              a: data.avgAssists.toFixed(1),
              kda: data.avgKda.toFixed(2),
            })}
          </p>
        </div>
      </section>

      {/* Top champions */}
      {data.topChampions.length > 0 && (
        <section>
          <p className="text-[10px] font-bold uppercase tracking-wider text-fg-muted">
            {t('stats.topChampions')}
          </p>
          <ul className="mt-1 flex flex-col gap-1">
            {data.topChampions.map((c) => (
              <li key={c.championId} className="text-sm text-fg">
                🏆 {c.championName ?? t('champion.unknown', { id: c.championId })} · {t('record.gamesShort', { games: c.games })} ·{' '}
                <span className={c.winRate >= 0.5 ? 'text-success' : 'text-fg-muted'}>
                  {(c.winRate * 100).toFixed(0)}% {t('stats.wr')}
                </span>
                {' · '}
                <span className="text-fg-muted">{c.kdaString}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Role distribution */}
      {data.roleDistribution.length > 0 && (
        <section>
          <p className="text-[10px] font-bold uppercase tracking-wider text-fg-muted">
            {t('stats.roles')}
          </p>
          <ul className="mt-1 flex flex-col gap-1">
            {data.roleDistribution.map((r) => {
              const label = r.position || 'UNKNOWN';
              const pct = data.matchesAnalyzed > 0
                ? (r.games / data.matchesAnalyzed) * 100
                : 0;
              return (
                <li
                  key={label}
                  className="flex items-baseline gap-2 text-sm text-fg"
                >
                  <span aria-hidden>{ROLE_EMOJI[r.position] ?? '❓'}</span>
                  <span className="font-medium">{label}</span>
                  <span className="text-fg-muted">
                    {t('record.gamesShort', { games: r.games })} · {pct.toFixed(0)}% · {(r.winRate * 100).toFixed(0)}% {t('stats.wr')}
                  </span>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      <p className="text-[10px] leading-relaxed text-fg-muted">{data.disclaimer}</p>
    </div>
  );
}
